import {
  BigQuerySyncSettings,
  BlobStorageSyncSettings,
  GCSSyncSettings,
  RedshiftSyncSettings,
  S3SyncSettings,
  SnowflakeSyncSettings,
  WarehouseProvider,
  WarehouseSyncStatus,
} from '@fullstory/proto/fs/services/warehauser';
import { assertExhaustive } from '@fullstory/typeutils';
import { WarehouseActionType } from './actions';
import { WarehouseRequestTypes } from './types';
import { timestamp } from '@fullstory/proto/google/protobuf/timestamp';

export interface WarehouseSyncStatusAndSettings<T> {
  status: WarehouseSyncStatus;
  updatedAt: timestamp | null;
  updatedBy?: string;
  settings: T;
}

export interface WarehouseSettingsState {
  bigQuery: WarehouseSyncStatusAndSettings<BigQuerySyncSettings> | null;
  snowflake: WarehouseSyncStatusAndSettings<SnowflakeSyncSettings> | null;
  redshift: WarehouseSyncStatusAndSettings<RedshiftSyncSettings> | null;
  s3: WarehouseSyncStatusAndSettings<S3SyncSettings> | null;
  gcs: WarehouseSyncStatusAndSettings<GCSSyncSettings> | null;
  blobStorage: WarehouseSyncStatusAndSettings<BlobStorageSyncSettings> | null;
}

const defaultState = {
  bigQuery: null,
  snowflake: null,
  redshift: null,
  s3: null,
  gcs: null,
  blobStorage: null,
};

export const warehouseSettings = (
  state: WarehouseSettingsState = defaultState,
  action: WarehouseActionType,
): WarehouseSettingsState => {
  switch (action.type) {
    case WarehouseRequestTypes.GetSyncSettingsSuccess: {
      const next: WarehouseSettingsState = {
        bigQuery: null,
        snowflake: null,
        redshift: null,
        s3: null,
        gcs: null,
        blobStorage: null,
      };

      action.data.settings.forEach(s => {
        if (s.bigQuery != null) {
          if (next.bigQuery != null)
            console.error('Received multiple BigQuery sync settings configurations');
          next.bigQuery = {
            status: s.status,
            updatedAt: s.updatedAt,
            updatedBy: s.updatedBy,
            settings: { ...s.bigQuery },
          };
        } else if (s.snowflake != null) {
          if (next.snowflake != null)
            console.error('Received multiple Snowflake sync settings configurations');
          next.snowflake = {
            status: s.status,
            updatedAt: s.updatedAt,
            updatedBy: s.updatedBy,
            settings: { ...s.snowflake },
          };
        } else if (s.redshift != null) {
          if (next.redshift != null)
            console.error('Received multiple Redshift sync settings configurations');
          next.redshift = {
            status: s.status,
            updatedAt: s.updatedAt,
            updatedBy: s.updatedBy,
            settings: { ...s.redshift },
          };
        } else if (s.s3 != null) {
          if (next.s3 != null) console.error('Received multiple S3 sync settings configurations');
          next.s3 = {
            status: s.status,
            updatedAt: s.updatedAt,
            updatedBy: s.updatedBy,
            settings: { ...s.s3 },
          };
        } else if (s.gcs != null) {
          if (next.gcs != null) console.error('Received multiple GCS sync settings configurations');
          next.gcs = {
            status: s.status,
            updatedAt: s.updatedAt,
            updatedBy: s.updatedBy,
            settings: { ...s.gcs },
          };
        } else if (s.blobStorage != null) {
          if (next.blobStorage != null)
            console.error('Received multiple Azure BlobStorage sync settings configurations');
          next.blobStorage = {
            status: s.status,
            updatedAt: s.updatedAt,
            updatedBy: s.updatedBy,
            settings: { ...s.blobStorage },
          };
        }
      });

      return next;
    }
    case WarehouseRequestTypes.DeleteSyncSettingsSuccess: {
      const provider =
        action.request.body?.provider ?? WarehouseProvider.WAREHOUSE_PROVIDER_INVALID;

      switch (provider) {
        case WarehouseProvider.WAREHOUSE_PROVIDER_BIG_QUERY:
          return { ...state, bigQuery: null };
        case WarehouseProvider.WAREHOUSE_PROVIDER_SNOWFLAKE:
          return { ...state, snowflake: null };
        case WarehouseProvider.WAREHOUSE_PROVIDER_INTERNAL:
          return state;
        case WarehouseProvider.WAREHOUSE_PROVIDER_REDSHIFT:
          return { ...state, redshift: null };
        case WarehouseProvider.WAREHOUSE_PROVIDER_S3:
          return { ...state, s3: null };
        case WarehouseProvider.WAREHOUSE_PROVIDER_GCS:
          return { ...state, gcs: null };
        case WarehouseProvider.WAREHOUSE_PROVIDER_BLOB_STORAGE:
          return { ...state, blobStorage: null };
        case WarehouseProvider.WAREHOUSE_PROVIDER_AGNES:
          return state;
        case WarehouseProvider.WAREHOUSE_PROVIDER_INVALID:
          return state;
        default:
          assertExhaustive(provider);
      }
    }
  }

  return state;
};
